.sn-navbar-search {
  display: none;

  // Unconditional on purpose. This element also carried the bare `me-container` class, which
  // supplied the gutter box in the one state where it is visible below 960px — when the
  // mobile burger menu is open (see the sibling selector below). That borrowed class is
  // invisible to component-deps.json, so those styles were only present because some other
  // block happened to pull in me-container. Same declarations, no cross-component dependency.
  @include container;

  @include desktop-lg {
    display: block;
    visibility: hidden;
    opacity: 0;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    transition: all 0.15s linear;

    @include container;

    &.is-visible {
      visibility: visible;
      opacity: 1;
      bottom: -48px;
    }
  }
}

// Reveal the navbar search when the mobile burger menu is open.
// The search is rendered as a sibling right AFTER #mobile-menu (via the base
// navbar's `area-search` slot) — it can't be nested inside #mobile-menu because
// on desktop that menu is transformed off-screen, which would break the desktop
// search dropdown. So key off the menu's open state with a sibling selector.
#mobile-menu.is-open ~ [slot='area-search'] .sn-navbar-search {
  display: block;
  width: 100%;
  z-index: 130;
  position: absolute;

  // Slide in from the left in sync with the mobile menu panel, which animates
  // `transform: translateX(-100%) → 0` over 0.3s ease-in-out. Using a keyframe
  // animation (not a transition) because it must run as the element switches
  // from `display: none` to `display: block`.
  animation: sn-navbar-search-slide-in 0.3s ease-in-out;
}

@keyframes sn-navbar-search-slide-in {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.sn-navbar-search__field {
  display: block;
  position: relative;
}

// When suggestions are visible, set z-index to match suggestions list
.sn-navbar-search.has-suggestions .sn-navbar-search__field {
  z-index: 10;
}

.sn-navbar-search__find,
.sn-navbar-search__clean {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.sn-navbar-search__find {
  left: 12px;
  display: block;
  width: 24px;
  height: 24px;

  @include desktop-lg {
    left: 18px;
  }
}

.sn-navbar-search__clean {
  display: block;

  @include size(24px);

  right: 8px;
}

.sn-navbar-search__input {
  border: var(--border-size-sm) solid var(--color-neutral-neutral);
  border-radius: var(--radius-size-sm);
  display: block;
  width: 100%;
  padding: var(--spacing-2xs) 36px;
  font-size: 14px;
  line-height: 24px;
  color: var(--text-color-primary);
  background-color: var(--background-color-primary);
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: var(--color-neutral-neutral);
  }

  &:focus {
    box-shadow:
      0 0 0 var(--border-size-sm) var(--color-neutral-white),
      0 0 0 var(--border-size-md) var(--color-brand-primary-main);
    outline: none;
  }

  @include desktop-lg {
    padding: var(--spacing-2xs) 48px;
  }
}

.sn-navbar-search.has-error {
  .sn-navbar-search__input {
    border-color: var(--color-system-error);
    box-shadow:
      0 0 0 var(--border-size-sm) var(--color-neutral-white),
      0 0 0 var(--border-size-md) var(--color-system-error);
    outline: none;
  }
}

.search-error {
  margin-top: var(--spacing-2xs);
  padding: var(--spacing-2xs) var(--spacing-xs);
  color: var(--color-system-error);
  font-size: 12px;
  line-height: 16px;
  opacity: 0;

  @include desktop-lg {
    position: absolute;
    top: 100%;
    left: var(--spacing-5xl);
    z-index: 2;
  }
}

.search-controls {
  display: none;
  align-items: center;
  gap: var(--spacing-2xs);
  padding: var(--spacing-2xs) var(--spacing-xs);
  font-size: 12px;
  line-height: 16px;
  color: var(--text-color-secondary);
  background-color: var(--background-color-primary);
  border-bottom-left-radius: var(--radius-size-sm);
  border-bottom-right-radius: var(--radius-size-sm);
  box-shadow: 0 2px 4px rgb(47 55 62 / 15%);

  &.is-visible {
    display: flex;
  }

  svg {
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
  }

  @include desktop-lg {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0 var(--spacing-5xl);
    z-index: 2;
  }
}

.suggestions-list {
  display: none;
  list-style: none;
  margin: 0;
  padding: var(--spacing-xs) 0;
  background-color: var(--background-color-primary);
  border: var(--border-size-sm) solid var(--border-color-secondary);
  border-radius: 0 0 var(--radius-size-sm) var(--radius-size-sm);
  box-shadow: 0 2px 4px rgb(47 55 62 / 15%);
  max-height: 240px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(-10px);
  transition:
    opacity 0.2s ease,
    visibility 0s linear 0.2s,
    transform 0.2s ease;

  &.is-visible {
    display: block;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition-delay: 0s;
    transform: translateY(0);

    // Match the search field's horizontal insets (margin: 0 15px) so the
    // suggestions align with the field and other burger-menu items instead of
    // spanning edge-to-edge. The desktop-lg block below overrides this with
    // absolute positioning once the navbar is truly desktop (> 960px).
  }

  @include desktop-lg {
    position: absolute;
    top: 100%;

    // Align with the search field / navbar content (64px insets), not 16px.
    left: var(--spacing-md);
    right: var(--spacing-md);
    z-index: 1;

    // On desktop the list is positioned with left/right insets, so drop the
    // mobile margin — otherwise it stacks on top of the insets (double padding).
    &.is-visible {
      margin: 0 auto;
    }
  }
}

.suggestions-item {
  margin: 0;
  padding: 0;
}

.suggestions-link {
  display: block;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: map-get($font-sizes, sm);
  line-height: var(--spacing-md);
  color: var(--text-color-primary);
  text-decoration: none;
  transition: background-color 0.15s ease;
  font-family: inherit;
  font-weight: map-get($font-weights, normal);
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  margin: 0;
  cursor: pointer;

  &:hover {
    background-color: var(--color-neutral-neutral-lighter);
  }

  &:focus {
    outline: none;
  }
}

// Selected item styling - same as hover
// Using higher specificity selector to ensure it applies
.suggestions-list .suggestions-item--selected > .suggestions-link,
.suggestions-list .suggestions-item--selected > .suggestions-link:hover {
  background-color: var(--color-neutral-neutral-lighter);
}

.suggestions-item--focused > .suggestions-link {
  background-color: var(--color-neutral-neutral-lighter);
  outline: none;
}

.accessibility-hidden-content {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.page-header.is-fixed {
  .sn-navbar-search {
    &.is-visible {
      bottom: -68px;
    }
  }
}

.suggestions-active {
  @include below-desktop {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='555' fill='none'%3E%3Crect width='218' height='12' fill='%23E0E6ED' rx='4'/%3E%3Crect width='320' height='12' y='28' fill='%23ECF0F4' rx='4'/%3E%3Crect width='270' height='12' y='48' fill='%23ECF0F4' rx='4'/%3E%3Crect width='188' height='12' y='76' fill='%23ECF0F4' rx='4'/%3E%3Crect width='218' height='12' y='124' fill='%23E0E6ED' rx='4'/%3E%3Crect width='320' height='12' y='152' fill='%23ECF0F4' rx='4'/%3E%3Crect width='270' height='12' y='172' fill='%23ECF0F4' rx='4'/%3E%3Crect width='188' height='12' y='200' fill='%23ECF0F4' rx='4'/%3E%3Crect width='218' height='12' y='248' fill='%23E0E6ED' rx='4'/%3E%3Crect width='320' height='12' y='276' fill='%23ECF0F4' rx='4'/%3E%3Crect width='270' height='12' y='296' fill='%23ECF0F4' rx='4'/%3E%3Crect width='188' height='12' y='324' fill='%23ECF0F4' rx='4'/%3E%3Crect width='218' height='12' y='372' fill='%23E0E6ED' rx='4'/%3E%3Crect width='320' height='12' y='400' fill='%23ECF0F4' rx='4'/%3E%3Crect width='270' height='12' y='420' fill='%23ECF0F4' rx='4'/%3E%3Crect width='188' height='12' y='448' fill='%23ECF0F4' rx='4'/%3E%3Crect width='218' height='12' y='496' fill='%23E0E6ED' rx='4'/%3E%3Crect width='320' height='12' y='524' fill='%23ECF0F4' rx='4'/%3E%3Crect width='270' height='12' y='544' fill='%23ECF0F4' rx='4'/%3E%3C/svg%3E");
    background-position: 16px;
    background-repeat: no-repeat;
    overflow: hidden;

    .me-dropdown,
    .me-button {
      opacity: 0;
    }
  }
}
